/**
 * VÉRAULT Portal — PortalBreadcrumb
 *
 * The standardized "where am I, how do I leave" affordance for every portal
 * surface. Elegant, minimal, mobile-friendly. Replaces the various ad-hoc
 * "← Back" links scattered across pages.
 *
 * Three pieces of information:
 *   1. A parent crumb (the natural "back" target) — required
 *   2. Optional grandparent crumb (for deeper surfaces like Journey > Logistics)
 *   3. Optional "Exit portal" link in the trailing corner, which leaves the
 *      portal entirely and returns to the public site
 *
 * Renders left-aligned, font-mono small-caps, with hover transitions.
 *
 * Usage:
 *   <PortalBreadcrumb
 *     parent={{ to: "/portal/advisor/journeys", label: "Journeys" }}
 *     trail={[{ to: "/portal/advisor/journeys/abc", label: "Italian honeymoon" }]}
 *     exitTo="/"
 *     exitLabel="Exit to website"
 *   />
 */
import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, LogOut } from "lucide-react";

export function PortalBreadcrumb({ parent, trail, exitTo, exitLabel = "Exit suite", testid = "portal-breadcrumb" }) {
  if (!parent) return null;
  const crumbs = Array.isArray(trail) && trail.length > 0 ? trail : [];
  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center justify-between gap-3 mb-8 sm:mb-10 -mt-1 opacity-70 hover:opacity-100 transition-opacity"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 overflow-hidden">
        <Link
          to={parent.to}
          className="inline-flex items-center gap-1.5 font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-colors group shrink-0"
          data-testid={`${testid}-parent`}
        >
          <ChevronLeft className="size-3 group-hover:-translate-x-0.5 transition-transform" strokeWidth={1.5} />
          {parent.label}
        </Link>
        {crumbs.map((c, i) => (
          <span key={i} className="flex items-center gap-2 sm:gap-3 min-w-0 overflow-hidden">
            <ChevronRight className="size-3 text-[hsl(var(--foreground-muted))]/35 shrink-0" strokeWidth={1.5} />
            {c.to ? (
              <Link
                to={c.to}
                className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-colors truncate"
                data-testid={`${testid}-crumb-${i}`}
              >
                {c.label}
              </Link>
            ) : (
              <span
                className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-secondary))] truncate"
                data-testid={`${testid}-crumb-${i}`}
              >
                {c.label}
              </span>
            )}
          </span>
        ))}
      </div>
      {exitTo && (
        <Link
          to={exitTo}
          className="inline-flex items-center gap-1.5 font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-colors shrink-0"
          data-testid={`${testid}-exit`}
          title={exitLabel}
        >
          <LogOut className="size-3" strokeWidth={1.5} />
          <span className="hidden sm:inline">{exitLabel}</span>
        </Link>
      )}
    </nav>
  );
}

export default PortalBreadcrumb;
